'use client';

import { useActionState } from 'react';
import { useRouter } from 'next/navigation';
import { useCartErrorStore, useCartPaymentFormStore } from 'stores/cart';
import { setPaymentAction } from '../../actions/payment/setPaymentAction';
import { getByFieldName } from '../../utils/payment/getByFieldName';
import { trackPaymentSubmitDone } from '../../analytics/payment';
import { PAYMENT_FORM_FIELD } from '../../payment/constants';
import type { PaymentActionState } from '../../payment/types';
import { showPaymentErrors } from '../../utils/payment/showPaymentErrors';

interface UseSetPaymentOptions {
  constants: Record<string, string>;
  checkoutRoute: string;
}

/** Everything after the action runs here, in the useActionState callback, so no effects are needed. */
export function useSetPayment({ constants, checkoutRoute }: UseSetPaymentOptions) {
  const router = useRouter();
  const setError = useCartErrorStore(store => store.setError);
  const setFieldErrors = useCartPaymentFormStore(store => store.setFieldErrors);

  const [state, action, isPending] = useActionState<PaymentActionState | null, FormData>(
    async (_previous, formData) => {
      const isPaymentNeeded = getByFieldName(formData, PAYMENT_FORM_FIELD.IS_PAYMENT_NEEDED) !== String(false);

      // Free cart via redeemed voucher.
      if (!isPaymentNeeded) {
        router.push(checkoutRoute);

        return { success: true };
      }

      const result = await setPaymentAction({ constants, formData });

      setFieldErrors(result.success ? {} : result.fieldErrors);

      if (result.success) {
        trackPaymentSubmitDone(getByFieldName(formData, PAYMENT_FORM_FIELD.TRACKING_METHOD));
        router.push(checkoutRoute);
      } else {
        showPaymentErrors(result, setError);
      }

      return result;
    },
    null
  );

  return { formAction: action, isPending, state };
}
